<div class="smart-search">
  <!-- Error State -->
  @if (shouldShowErrorState) {
    <div class="error-state-container">
      <ex-empty-state
        [svgIcon]="'close-circle-fill'"
        [title]="errorTitle ? (errorTitle | translate) : ('search.smart-search-result.error-default-title' | translate)"
        [paddingTop]="'medium'"
      >
        <div *exEmptyStateContent class="error-content">
          @if (errorContent) {
            <p class="error-secondary-text">
              {{ errorContent | translate }}
            </p>
          } @else {
            <p class="error-secondary-text">
              {{ 'search.smart-search-result.error-contact-support' | translate }}
            </p>
          }
          @if (!isNoRelevantDocumentsError) {
            <ex-button
              type="primary"
              variant="raised"
              size="medium"
              icon="arrow-clockwise-fill"
              (click)="retry()"
            >
              {{ 'common.retry' | translate }}
            </ex-button>
          }
        </div>
      </ex-empty-state>
    </div>
  } @else {
    <!-- Main Content -->
    <div class="content">
      <!-- Loading State -->
      @if (step != StepStatusEnum.COMPLETED) {
        <div class="search-loader">
          <mat-icon svgIcon="icon-wave-form"></mat-icon>
          <span>{{ getStepTranslationKey() | translate }}</span>
        </div>

      }

      <!-- Results -->
      @if (result && !isEmptySynthesis) {
        @if (result.title) {
          <span class="result-title">{{ result.title }}</span>
        }

        @if (result.stats) {
          <div class="result-summary">
            <span class="title">{{ "search.smart-search-result.about-analysis" | translate }}</span>
            <ul>
              <li>
                <span>
                  {{
                    "search.smart-search-result.documents-analyzed" | translate : {
                      count: totalDocuments,
                      plural: documentsPlural
                    }
                  }}
                  @if (hasDocumentsBreakdown) {
                    : {{ getDocumentBreakdown() }}
                  }
                </span>
              </li>
              @if (hasPeriod) {
                <li>
                  <span>
                    {{
                      "search.smart-search-result.period-covered" | translate : {
                        minDate: formattedMinDate,
                        maxDate: formattedMaxDate
                      }
                    }}
                  </span>
                </li>
              }
            </ul>
          </div>
        }

        @if (result.synthesis) {
          <div #synthesisContainer class="result-synthesis">
            <markdown #md class="markdown-content" [data]="result.synthesis" (ready)="onMarkdownRendered(md)"></markdown>
          </div>
        }
      }

      <!-- Synthesis Loading -->
      @if (step != StepStatusEnum.COMPLETED && !result && step !== StepStatusEnum.ERROR) {
        <div class="result-loaders">
          <ngx-skeleton-loader class="loader-1"/>
          <ngx-skeleton-loader class="loader-2"/>
        </div>
      }
    </div>

    <!-- Sources Sidebar -->
    <div class="right-side">
      @if (result?.resources?.length) {
        <div class="sources">
          <h2 class="sources-title">
            {{ totalResources }}
            {{ "search.smart-search-result.sources-used" | translate }}
          </h2>

          @if (pressArticles.length > 0) {
            <ng-container *ngTemplateOutlet="sourceSection; context: {
              icon: 'ph ph-newspaper ph-fill source-icon',
              title: pressArticles.length + ' ' + (getSourceTranslationKey('article', pressArticles.length) | translate),
              resources: visiblePressArticles,
              hasMore: hasMorePressArticles,
              showAll: showAllPressArticles,
              toggle: togglePressArticles.bind(this)
            }"></ng-container>
          }

          @if (administrativeDocs.length > 0) {
            <ng-container *ngTemplateOutlet="sourceSection; context: {
              icon: 'ph-fill ph-read-cv-logo',
              title: administrativeDocs.length + ' ' + (getSourceTranslationKey('admin_doc', administrativeDocs.length) | translate),
              resources: visibleAdministrativeDocs,
              hasMore: hasMoreAdministrativeDocs,
              showAll: showAllAdministrativeDocs,
              toggle: toggleAdministrativeDocs.bind(this)
            }"></ng-container>
          }

          @if (webContent.length > 0) {
            <ng-container *ngTemplateOutlet="sourceSection; context: {
              icon: 'ph ph-globe',
              title: webContent.length + ' ' + (getSourceTranslationKey('web_content', webContent.length) | translate),
              resources: visibleWebContent,
              hasMore: hasMoreWebContent,
              showAll: showAllWebContent,
              toggle: toggleWebContent.bind(this)
            }"></ng-container>
          }
        </div>
      }

      <!-- Sources Loading -->
      @if (step != StepStatusEnum.COMPLETED && !result) {
        <div class="sources">
          <h2 class="sources-title">{{ 'search.smart-search-result.sources' | translate }}</h2>
          <div class="source-loaders">
            <div class="loader-section">
              @for (i of [1, 2, 3, 4]; track i) {
                <ngx-skeleton-loader class="synthesis-loader-line"/>
              }
            </div>
            <div class="loader-section">
              @for (i of [1, 2, 3, 4]; track i) {
                <ngx-skeleton-loader class="synthesis-loader-line"/>
              }
            </div>

          </div>
        </div>
      }
    </div>
  }
</div>

<!-- Source Section Template -->
<ng-template #sourceSection let-icon="icon" let-title="title" let-resources="resources"
             let-hasMore="hasMore" let-showAll="showAll" let-toggle="toggle">
  <div class="source-section">
    <div class="source-section-header">
      <i [class]="icon"></i>
      <span class="source-section-title">{{ title }}</span>
    </div>
    <div class="source-list">
      @for (resource of resources; track resource.doc_id) {
        <ng-container *ngTemplateOutlet="referenceBadge; context: {
          index: getResourceIndex(resource),
          title: truncateTitle(resource.title),
          url: resource.url,
          resource: resource,
          titleAttr: resource.title,
          titleWithoutEm: removeEmTags(resource.title),
          source: resource.source || null,
          date: resource.publication_date ? formatDate(resource.publication_date) : '',
          excerpt: resource.excerpt || ''
        }"></ng-container>
      }
    </div>
    @if (hasMore) {
      <ex-button
        [icon]="showAll ? 'minus-square-fill' : 'plus-square-fill'"
        variant="ghost"
        (click)="toggle()"
        class="see-more-button"
      >
        {{ (showAll ? "search.smart-search-result.see-less" : "search.smart-search-result.see-more") | translate }}
      </ex-button>
    }
  </div>
</ng-template>

<!-- Reference Badge Template -->
<ng-template #referenceBadge let-index="index" let-title="title" let-url="url"
             let-resource="resource" let-titleAttr="titleAttr" let-titleWithoutEm="titleWithoutEm" let-source="source"
             let-date="date" let-excerpt="excerpt">
  <span class="source-link-wrapper" (click)="openUrl(url)">
    <span class="source-number reference-badge">{{ index }}</span>
    <span class="source-number reference-badge">-</span>
    @if (titleAttr) {
      <span class="source-title" [innerHTML]="titleWithoutEm"></span>
      <div class="source-reference-tooltip smart-search-tooltip smart-search-tooltip-positioned">
        <div class="tooltip-title" [innerHTML]="titleWithoutEm"></div>
        <div class="tooltip-meta">
          @if (date) {
            <div class="tooltip-date tooltip-date-visible">
              <mat-icon svgIcon="calendar-basic-600"></mat-icon>
              <span>{{ date }}</span>
            </div>
          }
          @if (source && resource.document_type !== 'admin_doc') {
            <div class="tooltip-source tooltip-source-visible">
              <mat-icon svgIcon="docs-basic-600"></mat-icon>
              <span>{{ source }}</span>
            </div>
          }
        </div>
        @if (excerpt) {
          <div class="tooltip-excerpt tooltip-excerpt-visible" [innerHTML]="excerpt"></div>
        }
      </div>
    }
  </span>
</ng-template>

